/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { createApi } from "@reduxjs/toolkit/query/react"
import { axiosBaseQuery } from "queries/apiClient"
import {
  MexcoreapipbListProductsResponse,
  MexcoreapipbGetItemsByProductIDsResponse,
  MexcoreapipbListCategoryTreeNodesResponse,
  MexcoreapipbProductSpu,
  MexcoreapipbProductSpuAttributeValue,
  MexcoreapipbEcomItemDetail,
  MexcoreapipbEcomItemAttributeValue,
} from "generated/mexcoreapi-sellercenter.type"
import { BusinessType } from "types/models/menu"
import { PAGINATION } from "../constants"
import { filterCategoryTreeNodes } from "../data/mockCategoryTreeData"
import {
  sellerCenterApiReducerPath,
  getSellerCenterApiUrl,
  sellerCenterEndpoints,
  SELLER_CENTER_CACHE_TAGS,
} from "./constants"

// Request/Response types for endpoint params and responses

interface ListProductsParams {
  merchantID: string
  limit?: number
  seek?: string
}

interface GetItemsByProductIDsParams {
  productSpuIDs: string[]
  merchantID: string
}

interface ListCategoryTreeNodesParams {
  countryID: number
  level?: number
  parentID?: string
  categoryTreeNodeIDs?: string[]
  name?: string
  loadAttributeClusters?: boolean
}

// Timestamp and auto-populated fields that the server manages — excluded from
// create requests to match the API contract (server rejects empty date strings).
type WithoutTimestamps<T> = Omit<
  T,
  "createdAt" | "updatedAt" | "deletedAt" | "availableAt"
>

type EcomItemAttributeValueForCreate = WithoutTimestamps<
  MexcoreapipbEcomItemAttributeValue
>

type EcomItemDetailForCreate = Omit<
  WithoutTimestamps<MexcoreapipbEcomItemDetail>,
  "ecomItemAttributeValues"
> & {
  ecomItemAttributeValues: EcomItemAttributeValueForCreate[]
}

// Create product request type — omits server-managed timestamps and the
// top-level productSpuID (only used for update, not for create).
export interface CreateProductRequest {
  merchantID: string
  categoryTreeNodeID: string
  productSpu: WithoutTimestamps<MexcoreapipbProductSpu>
  productSpuAttributeValues: WithoutTimestamps<
    MexcoreapipbProductSpuAttributeValue
  >[]
  ecomItemAndAttributes: EcomItemDetailForCreate[]
}

export interface CreateProductResponse {
  productSpuID?: string
}

// Video upload types (NOTE: mock endpoints; real endpoints will be updated once BE is ready.
// Galaxy-uploader pattern: get presigned URL -> upload to S3 -> poll status -> get video link + thumbnail.
// Mock video sample (video_sample.mp4) in useUploadVideo will be removed later.)

export interface GetVideoUploadUrlParams {
  fileExtension: string
}

export interface GetVideoUploadUrlResponse {
  uploadURL: string
  videoID: string
}

export interface GetVideoStatusParams {
  videoID: string
}

export enum VideoState {
  PROCESSING = "PROCESSING",
  PROCESSED = "PROCESSED",
  REJECTED = "REJECTED",
  FAILED = "FAILED",
}

export interface GetVideoStatusResponse {
  state: VideoState
  videoLink?: string
  thumbnailLink?: string
}

// API Slice

export const sellerCenterApi = createApi({
  reducerPath: sellerCenterApiReducerPath,
  baseQuery: axiosBaseQuery(getSellerCenterApiUrl()),
  tagTypes: [SELLER_CENTER_CACHE_TAGS.products],
  endpoints: builder => ({
    listProducts: builder.query<
      MexcoreapipbListProductsResponse,
      ListProductsParams
    >({
      query: ({ merchantID, limit, seek }) => ({
        url: sellerCenterEndpoints.listProducts,
        params: { merchantID, limit, seek },
      }),
      providesTags: [SELLER_CENTER_CACHE_TAGS.products],
    }),

    getItemsByProductIDs: builder.query<
      MexcoreapipbGetItemsByProductIDsResponse,
      GetItemsByProductIDsParams
    >({
      query: ({ productSpuIDs, merchantID }) => ({
        url: sellerCenterEndpoints.getItemsByProductIDs,
        params: {
          productSpuIDs,
          merchantID,
          limit: PAGINATION.DEFAULT_LIMIT_VARIANT_ITEMS,
        },
      }),
    }),

    listCategoryTreeNodes: builder.query<
      MexcoreapipbListCategoryTreeNodesResponse,
      ListCategoryTreeNodesParams
    >({
      queryFn: (params, _api, _extra, baseQuery) => {
        if (localStorage.getItem("ENABLE_CATEGORY_TREE_MOCK") !== "false") {
          const {
            level,
            parentID,
            categoryTreeNodeIDs,
            name,
            loadAttributeClusters,
          } = params
          let nodes = filterCategoryTreeNodes({
            level,
            parentID,
            name,
            categoryTreeNodeIDs,
          })
          if (!loadAttributeClusters) {
            nodes = nodes.map(n => ({ ...n, attributeClusters: [] }))
          }
          return new Promise(resolve =>
            setTimeout(
              () => resolve({ data: { categoryTreeNodes: nodes } }),
              300,
            ),
          )
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return baseQuery({
          url: sellerCenterEndpoints.listCategoryTreeNodes,
          params: { ...params, businessType: BusinessType.Shop },
        }) as any
      },
    }),

    createProduct: builder.mutation<
      CreateProductResponse,
      CreateProductRequest
    >({
      query: body => ({
        url: sellerCenterEndpoints.createProduct,
        method: "POST",
        data: body,
      }),
      invalidatesTags: [SELLER_CENTER_CACHE_TAGS.products],
    }),

    getVideoUploadUrl: builder.query<
      GetVideoUploadUrlResponse,
      GetVideoUploadUrlParams
    >({
      query: ({ fileExtension }) => ({
        url: sellerCenterEndpoints.getVideoUploadUrl,
        params: { fileExtension },
      }),
    }),

    getVideoStatus: builder.query<GetVideoStatusResponse, GetVideoStatusParams>(
      {
        query: ({ videoID }) => ({
          url: sellerCenterEndpoints.getVideoStatus(videoID),
        }),
      },
    ),
  }),
})

export const {
  useListProductsQuery,
  useGetItemsByProductIDsQuery,
  useListCategoryTreeNodesQuery,
  useLazyListCategoryTreeNodesQuery,
  useCreateProductMutation,
  useLazyGetVideoUploadUrlQuery,
  useLazyGetVideoStatusQuery,
} = sellerCenterApi
